import {useCallback, useEffect, useState} from 'react';

import {
  getProcessorOptionsFromCodes,
  getTransactionProcessorsOptions,
} from '@/constants/formatters';
import {processorService} from '@/services/processorService';
import type {AfriexTransactionProcessor} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';

interface ProcessorOption {
  key: string;
  value: string;
}

interface UseProcessorOptionsReturn {
  processorOptions: ProcessorOption[];
  isLoading: boolean;
  refetch: () => Promise<void>;
}

/**
 * Sources the processor dropdown options from the server
 * (GET /v2/admin/processors) so the admin list stays in sync with the
 * backend's AfriexTransactionProcessor enum and can't silently drift.
 *
 * Falls back to the local AfriexTransactionProcessor enum while loading, or if the
 * request fails, so the dropdown is never empty.
 */
export const useProcessorOptions = (
  processorsToExclude?: AfriexTransactionProcessor[],
): UseProcessorOptionsReturn => {
  const [processorCodes, setProcessorCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchProcessorCodes = useCallback(async () => {
    try {
      setIsLoading(true);
      const codes = await processorService.getProcessorAll();
      setProcessorCodes(codes ?? []);
    } catch (e) {
      showErrorMessage(
        `Fetching processor list failed:${getErrorMessage(e, String(e))}`,
      );
      setProcessorCodes([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fire-and-forget: fetchProcessorCodes catches and surfaces its own
    // failures (and falls back to the local enum), so this only stops an
    // unexpected throw becoming an unhandled rejection.
    fetchProcessorCodes().catch(() => {});
  }, [fetchProcessorCodes]);

  const processorOptions =
    processorCodes.length > 0
      ? getProcessorOptionsFromCodes(processorCodes, processorsToExclude)
      : getTransactionProcessorsOptions(processorsToExclude);

  return {
    processorOptions,
    isLoading,
    refetch: fetchProcessorCodes,
  };
};
